/**
 * Customer QR — Pass B (Rich UX).
 *
 * Layout:
 *   < md  : full-width, centered.
 *   >= md : max-w-md card, vertically centered with extra breathing room.
 *
 * Flourishes: gold-cornered frame around the QR, member id below in
 * monospace, instructions + safety footnote.
 */
import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { SkeletonBlock } from "../../components/Skeleton";
import { tierLabel } from "../../lib/tier";

const Corner = ({ pos }) => (
  <span
    aria-hidden
    className={[
      "absolute h-4 w-4 border-napoli-gold",
      pos === "tl" && "top-0 left-0 border-t-2 border-l-2",
      pos === "tr" && "top-0 right-0 border-t-2 border-r-2",
      pos === "bl" && "bottom-0 left-0 border-b-2 border-l-2",
      pos === "br" && "bottom-0 right-0 border-b-2 border-r-2",
    ]
      .filter(Boolean)
      .join(" ")}
  />
);

const QrSkeleton = () => (
  <div data-testid="customer-qr-skeleton" className="px-6 py-12 flex flex-col items-center">
    <SkeletonBlock className="h-3 w-40 mb-4" />
    <SkeletonBlock className="h-8 w-56 mb-10" />
    <SkeletonBlock className="h-[260px] w-[260px]" />
    <SkeletonBlock className="h-3 w-32 mt-6" />
  </div>
);

const CustomerQrPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customer/qr");
        if (!cancelled) setData(data);
      } catch (_) {
        // swallowed; skeleton stays which is fine
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return <QrSkeleton />;

  const tier = tierLabel(user?.current_tier || "ospite");
  const memberId = data.user_id.slice(0, 6).toUpperCase();

  return (
    <div data-testid="customer-qr" className="px-6 py-10 md:py-16 flex justify-center">
      <div className="w-full max-w-md">
        <div className="text-center">
          <p className="font-flourish text-napoli-oxblood text-3xl">il tuo codice</p>
          <p className="mt-1 font-body text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
            Show this to your server
          </p>
          <h1 className="mt-3 font-display font-bold text-2xl md:text-3xl text-napoli-espresso">
            {data.display_name}&rsquo;s code
          </h1>
        </div>

        <div
          data-testid="customer-qr-canvas"
          className="relative mt-8 bg-napoli-marble border border-napoli-gold/30 p-8 mx-auto w-fit"
        >
          <Corner pos="tl" />
          <Corner pos="tr" />
          <Corner pos="bl" />
          <Corner pos="br" />
          <QRCodeSVG
            value={data.qr_payload}
            size={240}
            bgColor="#FAF6EE"
            fgColor="#1F1611"
            level="M"
            includeMargin={false}
          />
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-napoli-espresso-soft">
            member · {memberId}
          </span>
          <span aria-hidden className="h-px w-6 bg-napoli-gold/40" />
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-napoli-gold/40 text-napoli-gold font-body text-[10px] tracking-[0.22em] uppercase">
            <span className="w-1 h-1 rounded-full bg-napoli-gold" aria-hidden />
            {tier}
          </span>
        </div>

        <div className="mt-10 border-t border-napoli-gold/20 pt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-napoli-oxblood">
            <Sparkles size={14} className="text-napoli-gold" />
            <p className="font-body text-[11px] tracking-[0.22em] uppercase">
              Earn points · Redeem rewards
            </p>
          </div>
          <p className="mt-3 font-body italic text-[12px] text-napoli-espresso-soft max-w-sm mx-auto">
            One scan after your meal. Points land on your card before the
            espresso cools.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerQrPage;
